import { FetchRequest, FetchResetStatus } from 'acscp/actions';
import { ResponseType } from 'acscp/api/responses';
import { FormattedMessage, FormattedNumber } from 'react-intl';
import { Dispatch } from 'redux';
import { PresentationComponentBase } from 'acscp/components/presentationComponentBase';
import { AppState } from 'acscp/store';
import React from 'react';
import { LoadingIndicator } from '../common/loadingIndicator';
import { LoyaltyOrderRequest, RequestClass } from 'acscp/api/requests';
import { AmendBasket, RemoveUnavailableGifts } from 'acscp/actions';
import { RequestStateProps, } from 'acscp/components/common/helpers/requestStateProps';
import style from './basketPage.module.scss';
import { connectWithRequests } from 'acscp/store/helpers/connectWithRequests';
import giftsComparison from 'acscp/store/helpers/sortGifts';
import { RequestErrorMessage } from 'acscp/components/common/requestErrorMessage';
import {
    BasketItem,
    getInvalidItemsInBasket,
    getTotalCostOfValidItemsInBasket,
    getValidItemsInBasket
} from 'acscp/store/helpers/basketHelper';
import { CachedImage } from 'acscp/components/common/cachedImage';
import { Background, Row } from 'acscp/components/common/row';
import classNames from 'classnames';
import { CrossIcon } from 'acscp/components/common/icons/crossIcon';

interface BasketPageStateProps {
    validItems: BasketItem[];
    totalPriceOfValidItems: number;
    balance: number;
    missingItems: BasketItem[];
    requestState: RequestStateProps;
    submitComplete: boolean;
}

interface BasketPageDispatchProps {
    submitOrder: (basket: BasketItem[]) => void;
    decrementQuantity: (giftId: string) => void;
    removeUnavailableGifts: () => void;
    clearRequestErrors: () => void;
}

interface BasketPageOwnProps {
    closeSelf: () => void;
}

export class BasketPagePresentational extends PresentationComponentBase<
    BasketPageOwnProps, BasketPageStateProps, BasketPageDispatchProps, {}> {
    render(): JSX.Element {
        return (
            <div className={style.BasketPage}>
                {this.renderBasketPageContent()}
            </div>
        );
    }

    renderBasketPageContent(): JSX.Element {
        if (this.props.submitComplete) {
            return (
                <div>
                    {this.renderAnyRequestErrors()}
                    <Row className={style.TextRow}>
                        <FormattedMessage
                            id={'components.gifts.basketPage.complete'}
                            defaultMessage={'Your request has been submitted and your Account Manager will be in touch shortly.'}
                        />
                    </Row>
                    {this.renderAnyBasketMessages()}
                    <button onClick={this.props.closeSelf} className={classNames(style.Button, style.ReturnButton)}>
                        <FormattedMessage
                            id={'components.gifts.basketPage.doneButton'}
                            defaultMessage={'DONE'}
                        />
                    </button>
                </div>
            );
        }

        if (this.props.requestState.isLoading) {
            return (
                <div className={style.LoadingIndicatorContainer}>
                    <LoadingIndicator />
                </div>
            );
        }

        if (this.props.validItems.length === 0) {
            return (
                <div>
                    {this.renderAnyRequestErrors()}
                    <Row className={style.TextRow}>
                        <FormattedMessage
                            id={'components.gifts.basketPage.empty'}
                            defaultMessage={'Your basket is empty.'}
                        />
                    </Row>
                    {this.renderAnyBasketMessages()}
                </div>
            );
        }

        return this.renderBasket();
    }

    renderBasket(): JSX.Element {
        const {validItems, totalPriceOfValidItems, balance} = this.props;
        const basketCostsTooMuch = totalPriceOfValidItems > balance;
        const canSubmitOrder = !basketCostsTooMuch;
        return (
            <div>
                {this.renderAnyRequestErrors()}
                <div className={style.BasketItemList}>
                    {validItems.sort((a, b) => giftsComparison(a.gift, b.gift)).map(item =>
                        [...Array(item.quantity).keys()].map(index => (
                            <div
                                className={style.BasketItem}
                                key={`${item.gift.id} ${index}`}
                            >
                                <div className={style.ImageContainer}>
                                    <CachedImage className={style.Image} src={item.gift.image ? item.gift.image.thumbnailUrl : undefined} />
                                </div>
                                <div className={style.ItemName}>
                                    {item.gift.name}
                                    <br/>
                                    <span className={style.ItemPrice}>
                                        <FormattedMessage
                                            id={'components.gifts.basketPage.price'}
                                            defaultMessage={'Points: {points, number}'}
                                            values={{points: item.gift.price}}
                                        />
                                    </span>
                                </div>
                                <CrossIcon
                                    className={style.RemoveItem}
                                    onClick={() => this.props.decrementQuantity(item.gift.id)}
                                    aria-label="Remove"
                                />
                            </div>
                        ))
                    )}
                </div>
                {validItems.length > 0 &&
                    <Row className={style.TextRow}>
                        <FormattedMessage
                            id={'components.gifts.basketPage.totalRewardPoints'}
                            defaultMessage={'TOTAL REWARD POINTS'}
                        />
                        <div className={style.TotalNumber}>
                            <FormattedNumber value={totalPriceOfValidItems} />
                        </div>
                    </Row>
                }
                {this.renderAnyBasketMessages()}
                {basketCostsTooMuch &&
                    <Row className={style.TextRow}>
                        <FormattedMessage
                            id={'components.gifts.basketPage.tooMuch'}
                            defaultMessage={'The cost of your basket exceeds your current points.'}
                        />
                    </Row>
                }
                {validItems.length > 0 &&
                    <button
                        className={`${style.Button} ${canSubmitOrder || style.Disabled}`}
                        onClick={() => canSubmitOrder && this.props.submitOrder(validItems)}
                        aria-disabled={!canSubmitOrder}
                    >
                        <FormattedMessage id={'components.gifts.basketPage.claimYourReward'} defaultMessage={'CLAIM YOUR REWARD'} />
                    </button>
                }
                {validItems.length > 0 &&
                    <Row className={style.TextRow} background={Background.LightestGrey}>
                        <FormattedMessage
                            id={'components.gifts.basketPage.remainingPoints'}
                            defaultMessage={'REMAINING POINTS'}
                        />
                        <div className={style.TotalNumber}>
                            <FormattedNumber value={balance - totalPriceOfValidItems} />
                        </div>
                    </Row>
                }
            </div>
        );
    }

    renderAnyRequestErrors(): JSX.Element|null {
        if (!this.props.requestState.requestError ||
            this.props.requestState.requestError.responseType !== ResponseType.RequestError) {
            return null;
        }

        return (
            <RequestErrorMessage requestState={this.props.requestState} />
        );
    }

    renderAnyBasketMessages(): JSX.Element|null {
        const {missingItems} = this.props;

        if (missingItems.length === 0) {
            return null;
        }

        return (
            <Row className={style.TextRow}>
                <FormattedMessage
                    id={'components.gifts.basketPage.unavailable'}
                    defaultMessage={'Some items are no longer available ' +
                    'and have been removed from your basket.'}
                />
            </Row>
                );
    }

    componentWillUnmount(): void {
        this.props.clearRequestErrors();
        this.props.removeUnavailableGifts();
    }
}

function mapStateToProps(
    state: AppState, ownProps: {}, getRequestError: (request: RequestClass<any>) => RequestStateProps
): BasketPageStateProps {
    return {
        validItems: getValidItemsInBasket(state.gifts),
        totalPriceOfValidItems: getTotalCostOfValidItemsInBasket(state.gifts),
        balance: state.gifts.balance || 0,
        missingItems: getInvalidItemsInBasket(state.gifts),
        requestState: getRequestError(LoyaltyOrderRequest),
        submitComplete: state.gifts.basketSubmitted,
    };
}

function mapDispatchToProps(dispatch: Dispatch): BasketPageDispatchProps {
    return {
        submitOrder: (basket: BasketItem[]) => {
            const apiBasket = basket.map(item => ({ giftId: item.gift.id, quantity: item.quantity}));
            dispatch(new FetchRequest(new LoyaltyOrderRequest(apiBasket)));
        },
        decrementQuantity: (giftId: string) => dispatch(new AmendBasket(giftId, -1)),
        removeUnavailableGifts: () =>  dispatch(new RemoveUnavailableGifts()),
        clearRequestErrors: () => dispatch(new FetchResetStatus(LoyaltyOrderRequest)),
    };
}

export const BasketPage = connectWithRequests(mapStateToProps, mapDispatchToProps)(BasketPagePresentational);
